import { faEdit, faSave, faTimes, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useCallback, useState, useMemo, useEffect } from "react"
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import { withAuth } from '../../api/withAuth';
import { formatDate } from "../CustomDateTools";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  SortingFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'

const GoldFinderApi = withAuth(axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_API_URL
}));

const columnHelper = createColumnHelper();




export function SoilModifierTable({ soilModifier, refreshData }) {
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [surfaceSampleTypes, setSurfaceSampleTypes] = useState([]);
  const [filters, setFilters] = useState({
    anumber: '',
    multiplier: '',
    description: '',
    surfacesampletype: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  useEffect(() => {
    fetchSurfaceSampleTypes();
  }, []);

  const fetchSurfaceSampleTypes = async () => {
    try {
      const response = await GoldFinderApi.get('/admin/surface-sample-types');
      setSurfaceSampleTypes(response.data.surfaceSampleTypes);
    } catch (error) {
      console.error('Error fetching surface sample types:', error);
    }
  };

  const handleEdit = (row) => {
    setEditingRow(row.anumber);
    setEditValues({
      multiplier: row.multiplier,
      description: row.description,
      surfacesampletype: row.surfacesampletype
    });
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const handleSave = async (anumber) => {
    setLoading(true);
    try {
      const response = await GoldFinderApi.put(`/admin/modifiers/soils/${anumber}`, {
        multiplier: editValues.multiplier,
        description: editValues.description,
        surfacesampletype: editValues.surfacesampletype
      });
      
      if (response.data.done) {
        setEditingRow(null);
        setEditValues({});
        if (refreshData) {
          refreshData(); // Refresh the data in parent component
        }
      }
    } catch (error) {
      console.error('Error updating modifier:', error);
      alert('Failed to update modifier. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDelete = async (anumber) => {
    const confirmMessage = `Are you sure you want to delete modifier for anumber: ${anumber}`;
    
    if (window.confirm(confirmMessage)) {
      setLoading(true);
      try {
        const response = await GoldFinderApi.delete(`/admin/modifiers/soils/${anumber}`);
        
        if (response.data.done) {
          if (refreshData) {
            refreshData(); // Refresh the data in parent component
          }
        }
      } catch (error) {
        console.error('Error deleting modifier:', error);
        alert('Failed to delete modifier. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️'; // Default sort icon
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Debug logging - check for duplicate anumbers which cause React key issues
  const anumberCounts = {};
  soilModifier.forEach(row => {
    anumberCounts[row.anumber] = (anumberCounts[row.anumber] || 0) + 1;
  });
  const duplicateAnumbers = Object.entries(anumberCounts).filter(([anumber, count]) => count > 1);
  
  if (duplicateAnumbers.length > 0) {
    console.warn('Found duplicate anumbers in modifier data:', duplicateAnumbers);
  }

  // Debug logging - log the data structure and filters
  if (filters.anumber && filters.anumber.trim() && soilModifier.length > 0) {
    console.log('Filter debug info:', {
      totalRows: soilModifier.length,
      currentFilters: filters,
      sampleRowData: soilModifier[0],
      sampleRowKeys: Object.keys(soilModifier[0] || {}),
      duplicateAnumbers: duplicateAnumbers.length
    });
  }

  const filteredModifiers = soilModifier.filter(row => {
    // Anumber filtering - handle both string and number types
    const anumberMatch = !filters.anumber || !filters.anumber.trim() || 
      String(row.anumber || '').includes(String(filters.anumber).trim());
    
    // Multiplier filtering 
    const multiplierMatch = !filters.multiplier || !filters.multiplier.trim() || 
      String(row.multiplier || '').includes(String(filters.multiplier).trim());
    
    // Description filtering
    const descriptionMatch = !filters.description || !filters.description.trim() || 
      String(row.description || '').toLowerCase().includes(String(filters.description).toLowerCase().trim());
    
    // Surface sample type filtering
    const surfaceTypeMatch = !filters.surfacesampletype || !filters.surfacesampletype.trim() || 
      String(row.surfacesampletype || '').toLowerCase().includes(String(filters.surfacesampletype).toLowerCase().trim());
    
    const matches = anumberMatch && multiplierMatch && descriptionMatch && surfaceTypeMatch;
    
    // Debug logging for rows that match the anumber filter
    if (filters.anumber && filters.anumber.trim() && anumberMatch) {
      console.log('Row matches anumber filter:', {
        filterValue: filters.anumber,
        rowAnumber: row.anumber,
        finalMatch: matches
      });
    }
    
    return matches;
  });

  // Debug: Log filtering results
  if (filters.anumber && filters.anumber.trim()) {
    console.log('Filtering results:', {
      originalCount: soilModifier.length,
      filteredCount: filteredModifiers.length,
      filterValue: filters.anumber,
      allAnumbers: soilModifier.map(row => row.anumber).slice(0, 10) // First 10 anumbers for reference
    });
  }

  const sortedAndFilteredModifiers = [...filteredModifiers].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Handle different data types
    if (sortConfig.key === 'multiplier') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return (
    <div style={{ 
      maxHeight: 'calc(100vh - 300px)', 
      overflowY: 'auto',
      border: '1px solid #dee2e6',
      borderRadius: '0.375rem'
    }}>
      <Table striped bordered hover style={{ marginBottom: 0 }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
          <tr>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('anumber')}
            >
              A Number {getSortIcon('anumber')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('multiplier')}
            >
              Multiplier {getSortIcon('multiplier')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('surfacesampletype')}
            >
              Surface Sample Type {getSortIcon('surfacesampletype')}
            </th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSort('description')}
            >
              Description {getSortIcon('description')}
            </th>
            <th style={{ width: '120px' }}>Actions</th>
          </tr>
          <tr>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.anumber}
                onChange={(e) => handleFilterChange('anumber', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.multiplier}
                onChange={(e) => handleFilterChange('multiplier', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.surfacesampletype}
                onChange={(e) => handleFilterChange('surfacesampletype', e.target.value)}
              />
            </th>
            <th>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Filter..."
                value={filters.description}
                onChange={(e) => handleFilterChange('description', e.target.value)}
              />
            </th>
            <th style={{ width: '120px' }}></th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredModifiers.map((row, index) => (
            <tr key={`${row.anumber}-${row.surfacesampletype}-${index}`}>
              <td>{row.anumber}</td>
              <td>
                {editingRow === row.anumber ? (
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={editValues.multiplier}
                    onChange={(e) => handleInputChange('multiplier', e.target.value)}
                    size="sm"
                  />
                ) : (
                  row.multiplier
                )}
              </td>
              <td>
                {editingRow === row.anumber ? (
                  <Form.Select
                    value={editValues.surfacesampletype}
                    onChange={(e) => handleInputChange('surfacesampletype', e.target.value)}
                    size="sm"
                  >
                    <option value="">Select type...</option>
                    {surfaceSampleTypes.map(type => (
                      <option key={type.surfacesampletypeid} value={type.surfacesampletypeid}>
                        {type.nicename}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  // Find and display the nice name for the current surfacesampletype
                  row.surfacesampletype === 'ALL' 
                    ? 'ALL' 
                    : surfaceSampleTypes.find(type => type.surfacesampletypeid === row.surfacesampletype)?.nicename || row.surfacesampletype
                )}
              </td>
              <td>
                {editingRow === row.anumber ? (
                  <Form.Control
                    type="text"
                    value={editValues.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    size="sm"
                  />
                ) : (
                  row.description
                )}
              </td>
              <td style={{ width: '120px', whiteSpace: 'nowrap' }}>
                {editingRow === row.anumber ? (
                  <>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleSave(row.anumber)}
                      disabled={loading}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faSave} />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleEdit(row)}
                      disabled={loading}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(row.anumber)}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}